export type ConnectionState = 'connecting' | 'live' | 'reconnecting' | 'offline'

export interface StreamHandlers {
  onEvent: (name: string, data: unknown, id: string | null) => void
  onState: (state: ConnectionState) => void
  /** Fired when the stream cannot be trusted any more and a refetch is due. */
  onResync: () => void
}

const WATCHDOG_MS = 60_000
const BACKOFF_MS = [1000, 2000, 5000, 10_000, 30_000]
const POLL_AFTER_FAILURES = 3

const EVENT_NAMES = [
  'snapshot',
  'list.updated',
  'item.created',
  'item.updated',
  'item.deleted',
  'list.deleted',
]

/**
 * EventSource with the failure modes a phone actually produces: sleeping tabs,
 * wifi handoffs that leave a half-open socket, and proxies that block SSE
 * altogether (in which case we fall back to polling).
 */
export class ListStream {
  private source: EventSource | null = null
  private lastEventId: string | null = null
  private failures = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private watchdog: ReturnType<typeof setTimeout> | null = null
  private pollTimer: ReturnType<typeof setInterval> | null = null
  private closed = false

  constructor(
    private readonly token: string,
    private readonly handlers: StreamHandlers,
  ) {}

  start(): void {
    this.closed = false
    this.connect()
    document.addEventListener('visibilitychange', this.onVisibility)
    window.addEventListener('online', this.onOnline)
    window.addEventListener('offline', this.onOffline)
  }

  stop(): void {
    this.closed = true
    this.teardown()
    this.stopPolling()
    document.removeEventListener('visibilitychange', this.onVisibility)
    window.removeEventListener('online', this.onOnline)
    window.removeEventListener('offline', this.onOffline)
  }

  private teardown(): void {
    this.source?.close()
    this.source = null
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    if (this.watchdog) clearTimeout(this.watchdog)
    this.reconnectTimer = null
    this.watchdog = null
  }

  private connect(): void {
    if (this.closed) return
    this.teardown()
    this.handlers.onState(this.failures === 0 ? 'connecting' : 'reconnecting')

    const source = new EventSource(`/api/lists/${this.token}/events`)
    this.source = source

    source.onopen = () => {
      this.failures = 0
      this.stopPolling()
      this.handlers.onState('live')
      this.resetWatchdog()
    }

    source.onerror = () => {
      if (this.closed) return
      this.failures++
      this.handlers.onState(navigator.onLine ? 'reconnecting' : 'offline')
      // EventSource retries on its own, but not predictably across browsers.
      source.close()
      this.scheduleReconnect()
      if (this.failures >= POLL_AFTER_FAILURES) this.startPolling()
    }

    source.onmessage = () => this.resetWatchdog()

    for (const name of EVENT_NAMES) {
      source.addEventListener(name, (event) => {
        this.resetWatchdog()
        const message = event as MessageEvent<string>
        if (message.lastEventId) this.lastEventId = message.lastEventId
        let data: unknown = null
        try {
          data = message.data ? JSON.parse(message.data) : null
        } catch {
          return
        }
        this.handlers.onEvent(name, data, message.lastEventId || null)
      })
    }
  }

  private scheduleReconnect(): void {
    if (this.closed || this.reconnectTimer) return
    const base = BACKOFF_MS[Math.min(this.failures - 1, BACKOFF_MS.length - 1)] ?? 30_000
    const jitter = Math.random() * 400
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, base + jitter)
  }

  /** A silent stream usually means a dead socket the browser has not noticed. */
  private resetWatchdog(): void {
    if (this.watchdog) clearTimeout(this.watchdog)
    this.watchdog = setTimeout(() => {
      this.failures++
      this.connect()
      this.handlers.onResync()
    }, WATCHDOG_MS)
  }

  private startPolling(): void {
    if (this.pollTimer) return
    this.pollTimer = setInterval(() => {
      if (document.visibilityState === 'visible') this.handlers.onResync()
    }, 15_000)
  }

  private stopPolling(): void {
    if (this.pollTimer) clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  private onVisibility = (): void => {
    if (document.visibilityState !== 'visible') return
    if (this.source?.readyState !== EventSource.OPEN) {
      this.failures = Math.max(1, this.failures)
      this.connect()
    }
    this.handlers.onResync()
  }

  private onOnline = (): void => {
    this.failures = 0
    this.connect()
    this.handlers.onResync()
  }

  private onOffline = (): void => {
    this.handlers.onState('offline')
  }

  get lastId(): string | null {
    return this.lastEventId
  }
}
